import React, { useContext, useState } from 'react';
import { Button, Divider, Grid, IconButton, TextField, Tooltip, Typography } from '@mui/material';
import TabPanel from "../TabPanel";
import { Controller, useForm } from "react-hook-form";
import { SettingsModalTabProps } from '../types';
import PasswordSection from './PasswordSection';
import ProfilePictureSection from './ProfilePictureSection';
import ColorLensIcon from '@mui/icons-material/ColorLens';
import AddIcon from '@mui/icons-material/Add';
import UserPreferences from './UserPreferences';
import { useMutation } from '@apollo/client/react';
import CurrentUserContext from '@/Contexts/CurrentUser';
import { UpdateUserProfileDocument, GetCurrentUserDocument, CreateUserMergeRequestDocument, ExportUserDataDocument } from '@/graphql/generated/graphql';
import { displayNotification } from '@/utils/notifications';
import { isValidEmail } from '@/utils/userInformation';
import EssealPhoneInput from '@/components/EssealPhoneInput';

interface ProfileFormValues {
  firstName: string;
  lastName: string;
  email: string;
  primaryPhone: string;
  bio: string;
  newPassword: string;
  confirmPassword: string;
};

const ProfileTab: React.FC<SettingsModalTabProps> = ({ parentTab, index }) => {
  const { currentUser } = useContext(CurrentUserContext);
  const [updateProfile] = useMutation(UpdateUserProfileDocument);
  const [createMergeUsers] = useMutation(CreateUserMergeRequestDocument, {
    onCompleted: () => {
      displayNotification({ type: "success", text: "Please Confirm email to merge the user." });
      setSourceEmail("");
    },
    onError: (errors) => displayNotification({ type: "error", text: errors.message })
  });
  const [exportUserData] = useMutation(ExportUserDataDocument, {
    onCompleted: () => {
      displayNotification({ type: "success", text: "User data export initiated. You will receive an email when it's ready." });
    },
    onError: (errors) => displayNotification({ type: "error", text: errors.message })
  });

  const [sourceEmail, setSourceEmail] = useState<string>("");

  const {
    // handleSubmit,
    reset: resetProfile,
    formState: { errors: profileErrors, isDirty },
    control,
    getValues,
  } = useForm<ProfileFormValues>({
    mode: "onChange",
    defaultValues: {
      firstName: currentUser?.firstName || "",
      lastName: currentUser?.lastName || "",
      email: currentUser?.email || "",
      primaryPhone: currentUser?.primaryPhone || "",
      bio: currentUser?.bio || "",
      newPassword: "",
      confirmPassword: "",
    }
  });

  const handleProfileUpdate = (data: ProfileFormValues) => {
    updateProfile({
      variables: {
        user: {
          id: currentUser?.id as string,
          ...data
        }
      },
      onCompleted: () => {
        displayNotification({
          type: "success",
          text: "Profile updated successfully",
        });
      },
      onError: () =>
        displayNotification({
          type: "error",
          text: "Unable to update the profile, try again later.",
        }),
      refetchQueries: [GetCurrentUserDocument],
    });
  };

  if (!currentUser) return <></>;

  return (
    <TabPanel value={parentTab} index={index} heading='Edit Profile' paddingX={2}>
      <ProfilePictureSection />
      <Grid
        container
        spacing={2}
        component="form"
        // onSubmit={handleSubmit(handleProfileUpdate)}
        justifyContent='flex-end'
        size="grow">
        <Grid size={6}>
          <Controller
            name="firstName"
            control={control}
            rules={{
              pattern: {
                value: /^.{2,50}$/,
                message: "First name must be 50 characters or less"
              }
            }}
            render={({ field }) => (
              <TextField
                fullWidth
                required
                label="First Name"
                {...field}
                margin="normal"
                variant="outlined"
                error={!!profileErrors.firstName}
                helperText={profileErrors.firstName?.message}
              />
            )}
          />
        </Grid>
        <Grid size={6}>
          <Controller
            name="lastName"
            control={control}
            rules={{
              pattern: {
                value: /^.{2,50}$/,
                message: "Last name must be 50 characters or less"
              }
            }}
            render={({ field }) => (
              <TextField
                fullWidth
                required
                {...field}
                label="Last Name"
                margin="normal"
                variant="outlined"
                error={!!profileErrors.lastName}
                helperText={profileErrors.lastName?.message}
              />
            )}
          />
        </Grid>
        <Grid size={6}>
          <Controller
            name="email"
            control={control}
            rules={{
              pattern: {
                value: /^[\w+.-]+@[a-z\d.-]+\.[a-z]+$/i,
                message: 'Invalid email format',
              },
            }}
            render={({ field }) => (
              <TextField
                fullWidth
                required
                {...field}
                label="Email"
                margin="normal"
                variant="outlined"
                error={!!profileErrors.email}
                helperText={profileErrors.email?.message}
              />
            )}
          />
        </Grid>
        <Grid size={6}>
          <Controller
            name="primaryPhone"
            control={control}
            rules={{
              pattern: {
                value: /^\+44 \([0-9]{2}\) [0-9]{3} [0-9]{5}$/,
                message: 'Invalid phone number',
              },
            }}
            render={({ field }) =>
              <EssealPhoneInput
                defaultCountry='GB'
                defaultValue={field.value}
                size='lg'
                label="Primary Phone"
                onChange={(e164) => field.onChange(e164)}
              />
            }
          />
        </Grid>
      </Grid>
      <Grid
        container
        maxWidth={'40px !important'}
        justifyContent='center'
        alignItems='center'
        size="grow">
        <Tooltip title="Theme: Future Feature"><IconButton sx={{ width: 25, height: 25 }}><ColorLensIcon /></IconButton></Tooltip>
        <Tooltip title="Quick Action: Future Feature"><IconButton sx={{ width: 25, height: 25 }}><AddIcon /></IconButton></Tooltip>
        <Tooltip title="Quick Action: Future Feature"><IconButton sx={{ width: 25, height: 25 }}><AddIcon /></IconButton></Tooltip>
      </Grid>
      <Grid mt={1} size={12}>
        <Controller
          name="bio"
          control={control}
          rules={{
            pattern: {
              value: /^.{0,1000}$/,
              message: "Character limit reached, 1000 max."
            }
          }}
          render={({ field }) => (
            <TextField
              fullWidth
              {...field}
              label="Bio"
              variant="outlined"
              multiline
              rows={4}
              error={!!profileErrors.bio}
              helperText={profileErrors.bio?.message}
            />
          )}
        />
        <PasswordSection
          control={control}
          resetProfile={resetProfile}
          newPasswordError={profileErrors.newPassword}
          confirmNewPassword={profileErrors.confirmPassword}
          getValues={getValues}
        />

        {currentUser.isOwner &&
          <>
            <Divider sx={{ my: 3 }} />
            <Button
              variant="contained"
              color="primary"
              onClick={() => exportUserData()}
            >
              Export User Data
            </Button>
          </>
        }

        <Divider sx={{ my: 3 }} />
        <Typography variant="subtitle1">Enter the email of the target user you wish to merge</Typography>
        <TextField
          fullWidth
          label="Email"
          margin="normal"
          variant="outlined"
          onChange={(e) => setSourceEmail(e.target.value)}
          error={sourceEmail.length > 0 && !isValidEmail(sourceEmail)}
          helperText={sourceEmail.length > 0 && !isValidEmail(sourceEmail) ? "Invalid email format" : ""}
        />
        {
          sourceEmail && (
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                createMergeUsers({ variables: { sourceEmail } })
              }}
              disabled={!isValidEmail(sourceEmail)}
            >
              Merge Account
            </Button>
          )
        }
        <Divider sx={{ my: 3 }} />

        <UserPreferences />
      </Grid>
      {/* <NotifyAboutChanges hasChanged={isDirty} handleReset={() => resetProfile()} handleSave={handleSubmit(handleProfileUpdate)} /> */}
      {isDirty &&
        <Grid size={12} container justifyContent='flex-end'>
          <Button onClick={() => resetProfile()}>Reset</Button>
          <Button
            variant='outlined'
            onClick={() => {
              handleProfileUpdate(getValues())
            }}
          >
            Save
          </Button>
        </Grid>
      }
    </TabPanel>
  );
}

export default ProfileTab;
